import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
  useMediaQuery
} from '@mui/material';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import PersonSearchRoundedIcon from '@mui/icons-material/PersonSearchRounded';
import SchoolRoundedIcon from '@mui/icons-material/SchoolRounded';
import { useTheme } from '@mui/material/styles';
import PageHeader from '../PageHeader';
import MonCarnet from './MonCarnet';
import api from '../../api';

export default function TeacherMuscuCarnetViewer() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [classes, setClasses] = useState([]);
  const [selectedClasse, setSelectedClasse] = useState('');
  const [eleves, setEleves] = useState([]);
  const [selectedEleve, setSelectedEleve] = useState('');
  const [loadingClasses, setLoadingClasses] = useState(true);
  const [loadingEleves, setLoadingEleves] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let mounted = true;

    const loadClasses = async () => {
      setLoadingClasses(true);
      setError('');
      try {
        const res = await api.get('/classes');
        if (!mounted) return;
        const list = Array.isArray(res.data) ? res.data : [];
        setClasses(list);
      } catch (err) {
        if (!mounted) return;
        setError(err.response?.data?.error || 'Erreur lors du chargement des classes');
        setClasses([]);
      } finally {
        if (mounted) setLoadingClasses(false);
      }
    };

    loadClasses();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    let mounted = true;

    const loadEleves = async () => {
      if (!selectedClasse) {
        setEleves([]);
        setSelectedEleve('');
        return;
      }

      setLoadingEleves(true);
      setError('');
      try {
        const res = await api.get(`/eleves?classe=${encodeURIComponent(selectedClasse)}`);
        if (!mounted) return;
        const list = Array.isArray(res.data) ? res.data : [];
        setEleves(list);
        setSelectedEleve((current) => (list.some((e) => e._id === current) ? current : ''));
      } catch (err) {
        if (!mounted) return;
        setError(err.response?.data?.error || 'Erreur lors du chargement des élèves');
        setEleves([]);
        setSelectedEleve('');
      } finally {
        if (mounted) setLoadingEleves(false);
      }
    };

    loadEleves();
    return () => { mounted = false; };
  }, [selectedClasse]);

  const classeName = useMemo(() => {
    return classes.find((c) => c._id === selectedClasse)?.nom || '';
  }, [classes, selectedClasse]);

  const selectedEleveInfo = useMemo(() => {
    return eleves.find((e) => e._id === selectedEleve) || null;
  }, [eleves, selectedEleve]);

  return (
    <Box sx={{ px: { xs: 1.25, sm: 3 }, py: { xs: 1.5, sm: 4 }, maxWidth: 1040, mx: 'auto' }}>
      <PageHeader
        title="Suivi muscu des élèves"
        subtitle="Choisir une classe puis un élève pour consulter le détail de son carnet et sa progression séance par séance."
        backTo="/apps/carnet-muscu"
        homeTo="/"
      />

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Stack spacing={2} sx={{ mb: 2.5 }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 1.5, sm: 2 },
            borderRadius: { xs: 3, sm: 4 },
            boxShadow: '0 10px 22px rgba(32,24,64,0.08)',
            background: 'linear-gradient(135deg, #fff4ea 0%, #fffaf5 100%)',
            border: '1px solid rgba(210,132,62,0.16)'
          }}
        >
          <Stack direction="row" spacing={{ xs: 1.25, sm: 1.5 }} alignItems="flex-start">
            <Box
              sx={{
                width: { xs: 44, sm: 52 },
                height: { xs: 44, sm: 52 },
                borderRadius: { xs: 2.5, sm: 3 },
                background: 'linear-gradient(135deg, rgba(210,132,62,0.12) 0%, rgba(255,209,168,0.22) 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.65)',
                flexShrink: 0
              }}
            >
              <GroupsRoundedIcon sx={{ fontSize: 30 }} />
            </Box>

            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 800, mb: 0.5, fontSize: { xs: '0.98rem', sm: '1.05rem' } }}>
                Choisir une classe
              </Typography>
              <Typography color="text.secondary" sx={{ fontSize: { xs: '0.84rem', sm: '0.92rem' }, lineHeight: 1.32, mb: 1.5 }}>
                Sélectionne une classe pour afficher la liste des élèves et accéder à leur synthèse muscu.
              </Typography>

              {loadingClasses ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : (
                <FormControl fullWidth>
                  <InputLabel id="muscu-prof-classe-label">Classe</InputLabel>
                  <Select
                    labelId="muscu-prof-classe-label"
                    label="Classe"
                    value={selectedClasse}
                    onChange={(e) => setSelectedClasse(e.target.value)}
                  >
                    <MenuItem value="">Choisir une classe</MenuItem>
                    {classes.map((classe) => (
                      <MenuItem key={classe._id} value={classe._id}>{classe.nom}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Box>
          </Stack>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            p: { xs: 1.5, sm: 2 },
            borderRadius: { xs: 3, sm: 4 },
            boxShadow: '0 10px 22px rgba(32,24,64,0.08)',
            background: 'linear-gradient(135deg, #eef7ff 0%, #f8fbff 100%)',
            border: '1px solid rgba(51,120,220,0.16)'
          }}
        >
          <Stack direction="row" spacing={{ xs: 1.25, sm: 1.5 }} alignItems="flex-start">
            <Box
              sx={{
                width: { xs: 44, sm: 52 },
                height: { xs: 44, sm: 52 },
                borderRadius: { xs: 2.5, sm: 3 },
                background: 'linear-gradient(135deg, rgba(51,120,220,0.12) 0%, rgba(156,205,255,0.2) 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.65)',
                flexShrink: 0
              }}
            >
              <PersonSearchRoundedIcon sx={{ fontSize: 30 }} />
            </Box>

            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 800, mb: 0.5, fontSize: { xs: '0.98rem', sm: '1.05rem' } }}>
                Choisir un élève
              </Typography>
              <Typography color="text.secondary" sx={{ fontSize: { xs: '0.84rem', sm: '0.92rem' }, lineHeight: 1.32, mb: 1.5 }}>
                Une fois l’élève choisi, tu arrives directement sur une synthèse détaillée de toutes ses séances.
              </Typography>

              {loadingEleves ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : (
                <FormControl fullWidth disabled={!selectedClasse || eleves.length === 0}>
                  <InputLabel id="muscu-prof-eleve-label">Élève</InputLabel>
                  <Select
                    labelId="muscu-prof-eleve-label"
                    label="Élève"
                    value={selectedEleve}
                    onChange={(e) => setSelectedEleve(e.target.value)}
                  >
                    <MenuItem value="">Choisir un élève</MenuItem>
                    {eleves.map((eleve) => (
                      <MenuItem key={eleve._id} value={eleve._id}>{eleve.prenom} {eleve.nom}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {selectedEleveInfo && !isMobile && (
                <Typography variant="body2" sx={{ mt: 1, fontWeight: 600 }}>
                  Élève sélectionné : {selectedEleveInfo.prenom} {selectedEleveInfo.nom}{classeName ? ` — ${classeName}` : ''}
                </Typography>
              )}
            </Box>
          </Stack>
        </Paper>
      </Stack>

      {(classeName || selectedEleveInfo) && (
        <Paper
          elevation={0}
          sx={{
            p: { xs: 1.25, sm: 1.5 },
            mb: 2,
            borderRadius: { xs: 3, sm: 4 },
            background: 'linear-gradient(135deg, #f7f9fc 0%, #ffffff 100%)',
            border: '1px solid rgba(63,61,86,0.08)'
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <SchoolRoundedIcon sx={{ color: '#52627a' }} />
            {classeName && <Chip label={`Classe : ${classeName}`} sx={{ fontWeight: 700 }} />}
            {selectedEleveInfo && <Chip color="primary" label={`Élève : ${selectedEleveInfo.prenom} ${selectedEleveInfo.nom}`} sx={{ fontWeight: 700 }} />}
          </Stack>
        </Paper>
      )}

      {selectedEleve ? (
        <MonCarnet eleveId={selectedEleve} readOnly />
      ) : (
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: { xs: 3, sm: 4 },
            boxShadow: '0 10px 22px rgba(32,24,64,0.08)',
            border: '1px solid rgba(63,61,86,0.08)'
          }}
        >
          <Typography color="text.secondary" sx={{ textAlign: 'center' }}>
            Sélectionne d’abord une classe puis un élève pour afficher son carnet détaillé et sa progression.
          </Typography>
        </Paper>
      )}
    </Box>
  );
}